import { Button, Menu } from '@mantine/core'
import { useLocalStorage } from '@mantine/hooks'
import {
  IconCamera,
  IconChevronDown,
  IconPhoto,
  IconVideo,
} from '@tabler/icons-react'
import { useRef } from 'react'

interface AddPhotoButtonProps {
  onFileSelect: (file: File) => void
  loading?: boolean
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'compact-md'
  variant?: string
  allowVideo?: boolean
}

export function AddPhotoButton({
  onFileSelect,
  loading = false,
  size = 'xs',
  variant = 'outline',
  allowVideo = false,
}: AddPhotoButtonProps) {
  const cameraInputRef = useRef<HTMLInputElement>(null)
  const galleryInputRef = useRef<HTMLInputElement>(null)
  const videoInputRef = useRef<HTMLInputElement>(null)

  // Global localStorage preference - shared across all instances
  const [lastAction, setLastAction] = useLocalStorage<
    'camera' | 'gallery' | 'video'
  >({
    key: 'photo-upload-method',
    defaultValue: 'camera',
  })

  // Fall back to camera if video was last action but video is not allowed
  const effectiveLastAction =
    lastAction === 'video' && !allowVideo ? 'camera' : lastAction

  const handleAction = (action: 'camera' | 'gallery' | 'video') => {
    setLastAction(action)
    if (action === 'camera') {
      cameraInputRef.current?.click()
    } else if (action === 'video') {
      videoInputRef.current?.click()
    } else {
      galleryInputRef.current?.click()
    }
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      onFileSelect(file)
      e.target.value = '' // Reset to allow selecting the same file again
    }
  }

  const Icon =
    effectiveLastAction === 'camera'
      ? IconCamera
      : effectiveLastAction === 'video'
        ? IconVideo
        : IconPhoto

  const acceptTypes = allowVideo ? 'image/*,video/*' : 'image/*'

  return (
    <>
      {/* Hidden camera input */}
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />
      {/* Hidden gallery input */}
      <input
        ref={galleryInputRef}
        type="file"
        accept={acceptTypes}
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />
      {/* Hidden video input */}
      {allowVideo && (
        <input
          ref={videoInputRef}
          type="file"
          accept="video/*"
          capture="environment"
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />
      )}

      {/* Split-button UI */}
      <Button.Group>
        <Button
          variant={variant}
          size={size}
          leftSection={variant === 'subtle' ? null : <Icon size={14} />}
          onClick={() => handleAction(effectiveLastAction)}
          loading={loading}
          style={{ minHeight: size === 'compact-md' ? 40 : undefined }}
        >
          {allowVideo ? 'Lisää' : 'Lisää kuva'}
        </Button>
        <Menu position="bottom-end">
          <Menu.Target>
            <Button
              variant={variant}
              size={size}
              style={{
                borderLeft: 'none',
                minHeight: size === 'compact-md' ? 40 : undefined,
              }}
            >
              <IconChevronDown size={12} />
            </Button>
          </Menu.Target>
          <Menu.Dropdown>
            <Menu.Item
              leftSection={<IconCamera size={16} />}
              onClick={() => handleAction('camera')}
            >
              Ota kuva
            </Menu.Item>
            {allowVideo && (
              <Menu.Item
                leftSection={<IconVideo size={16} />}
                onClick={() => handleAction('video')}
              >
                Kuvaa video
              </Menu.Item>
            )}
            <Menu.Item
              leftSection={<IconPhoto size={16} />}
              onClick={() => handleAction('gallery')}
            >
              Valitse galleriasta
            </Menu.Item>
          </Menu.Dropdown>
        </Menu>
      </Button.Group>
    </>
  )
}
